import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'cropperjs';

import 'cropperjs/dist/cropper.css';

/**
 * Hides the seed image, which cropper.js leaves in place behind the container it builds. Static so
 * it is not a new object every render.
 */
const IMAGE_STYLE = { opacity: 0, maxWidth: '100%' };

/**
 * Wraps cropper.js around an image.
 *
 * cropper.js is driven directly rather than through `react-cropper`, whose `applyDefaultOptions`
 * runs inside the `ready` handler and unconditionally applies its `scaleX` / `scaleY` props. Those
 * default to `1`, which overwrites the mirror cropper.js derives from the photo's Exif orientation
 * while leaving the rotation it derived alongside it in place - so an orientation carrying a mirror
 * (2, 4, 5, 7) is rendered rotated but not mirrored, and reads as flipped. Only the crop box and a
 * ready callback are needed here, so the wrapper is not worth the bug.
 */
const ImageCrop = ({
  onCropperChange,
  aspectRatio,
  imageUrl,
  style,
  viewMode,
}) => {
  const imageRef = useRef(null);

  /*
    Read through a ref so an inline callback - which every caller passes - does not tear the cropper
    down and rebuild it on each render of the parent.
  */
  const onCropperChangeRef = useRef(onCropperChange);
  onCropperChangeRef.current = onCropperChange;

  /*
    Keyed on the source alone. cropper.js reads the rest once at construction and the callers pass
    them as constants, so rebuilding on one of them changing identity would only discard the crop
    the user has already dragged.
  */
  useEffect(() => {
    const cropper = new Cropper(imageRef.current, {
      aspectRatio,
      guides: false,
      background: false,
      viewMode,
    });
    onCropperChangeRef.current(cropper);
    return () => cropper.destroy();
  }, [imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={style}>
      {/*
        cropper.js hides this element and builds its own container next to it, so it is only ever the
        seed. `maxWidth` keeps it from widening the frame in the moment before that happens.
      */}
      <img src={imageUrl} alt="" ref={imageRef} style={IMAGE_STYLE} />
    </div>
  );
};

ImageCrop.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  onCropperChange: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  /**
   * Applied to the wrapper this renders. Without a height here, cropper.js sizes itself to the
   * image's natural dimensions and overflows whatever container it is placed in.
   */
  style: PropTypes.shape({}),
  /** cropper.js `viewMode`. 1 keeps the crop box inside the image; 0 (the default) lets it escape. */
  viewMode: PropTypes.oneOf([0, 1, 2, 3]),
};

ImageCrop.defaultProps = {
  aspectRatio: null,
  style: undefined,
  viewMode: 0,
};

export default ImageCrop;
